import {useRef, useState} from 'react'
import {Link} from 'react-router'

import {useHeaderReveal} from '@core/ui/useHeaderReveal'
import {HeaderSearch, type HeaderSearchProps} from './HeaderSearch'

import styles from './Header.module.scss'

/** What the wall hands the header: its `q` and the setter (`Wall.tsx`'s `renderHeader`). Whether
 *  the field is folded is the header's own business, so the fold callback is not a caller's. */
export type HeaderSearchSlot = Omit<HeaderSearchProps, 'onExpandedChange' | 'onPanelChange' | 'phone'>

export type HeaderProps = {
    /** The home link's content — a house's own mark, plus wordmark if it has one. */
    brand: React.ReactNode
    /** Omitted by a house with nothing to search from this state. */
    search?: HeaderSearchSlot
    /** A house's own nav doors. Omitted entirely for a house with none (Ovid — its pack pages are
     *  cut on this surface). */
    nav?: React.ReactNode
    /** The right rail — the sign-in door, or the account menu once signed in. */
    right: React.ReactNode
}

/**
 * Header — the shared wall header shell (`core/wall/ui`): sticky, scroll-reveal
 * (`useHeaderReveal`, `@core/ui`), a brand slot, the wall's own `HeaderSearch`, an optional nav slot
 * and a right rail. Lifted out of Toile's own `components/Header` (plan `2026_09_17_ovid_one_wall.md`)
 * so a second house (Ovid) renders the same shell instead of forking it — a house supplies its own
 * `brand`/`nav`/`right` content; this file owns the chrome around them.
 *
 * Transposed from the Chrome.dc.html design pass (thinking/design/toile_surfaces/, 2026-08-23):
 * sticky, `search` fills the slot reserved for the wall — a caller with nothing to search simply omits
 * it (BRIEF "top edge is chrome, it never moves" still holds: the header itself never grows a search
 * band, it just carries the field or doesn't).
 *
 * STICKY, AT LAST (plan `2026_09_04_wall_header_reveal.md` § Step 0 — the incident and its fix live
 * one layer down, in each house's own stylesheet). Once sticky actually stuck, `useHeaderReveal`
 * adds the reveal on top of it: `hidden` becomes `.headerHidden` (`transform: translateY(-100%)`,
 * the header never leaves the flow, never reflows the wall beneath it — `Wall.tsx`'s windowing reads
 * `getBoundingClientRect`, untouched by a transform); `atTop` becomes `.headerFloating`'s shadow,
 * present only once the header is floating over content rather than sitting above it. Passing
 * `headerRef` is the whole of the wiring the hook needs — it holds itself shown while focus is
 * inside it, off its own listeners, with no separate wiring needed here for the search field, a Tab
 * into the header, or an account menu staying open.
 *
 * No mount-time transition guard is needed for edge case 8 ("no transition on mount"): the hook's
 * `hidden` always starts `false` — a restored deep scroll position never flips it — so there is
 * never a mount-time `.headerHidden` toggle for the CSS transition to animate away in the first
 * place; only `atTop`'s shadow can differ at mount, and that carries no transition of its own.
 *
 * The search panel on a phone is a sheet fixed over the screen, and a fixed box is placed against the nearest ancestor
 * that blurs or transforms what is behind it: while the sheet is open the header is neither (`.headerSheet`).
 *
 * `search` always stands open at rest on a phone (`phone="open"`) rather than folding to a glyph —
 * a magnifier reads "filter," the wall's thesis is "make" (BRIEF § Iteration II item 5). A house's
 * own mobile rules (hiding its wordmark, moving a nav door into its account menu) are that house's
 * own stylesheet's business; this file only ever hides its own `.nav` slot below 640px.
 * `HeaderSearch`'s `phone: 'fold'` stays available on the component itself for a future caller that
 * wants it (code-quality review F10).
 */
export function Header({brand, search, nav, right}: HeaderProps) {
    const headerRef = useRef<HTMLElement>(null)
    const {hidden, atTop} = useHeaderReveal(headerRef)
    // The search panel as a sheet over the screen (phone): the header drops what would hold the sheet inside its own box.
    const [sheetOpen, setSheetOpen] = useState(false)

    const className = [
        styles.header,
        hidden ? styles.headerHidden : null,
        !atTop ? styles.headerFloating : null,
        sheetOpen ? styles.headerSheet : null,
    ].filter(Boolean).join(' ')

    return (
        <header ref={headerRef} className={className}>
            <Link to="/" className={styles.brand}>
                {brand}
            </Link>
            {search && <HeaderSearch {...search} phone="open" onPanelChange={setSheetOpen} />}
            {nav && <nav className={styles.nav}>{nav}</nav>}
            <div className={styles.right}>{right}</div>
        </header>
    )
}
